import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Label } from '../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Shield, Plus, Edit, Trash2, Users, Download, Lock, Unlock, MessageCircle, ExternalLink, ChevronDown, ChevronUp, ArrowUpDown, UserMinus } from 'lucide-react';

export const TeamsTab = ({
  tournament, teams, filteredTeams, expandedTeamId, setExpandedTeamId,
  teamsAgeFilter, setTeamsAgeFilter, teamRosterSort, toggleRosterSort, sortTeamRoster,
  onCreateTeam, onEditTeam, onDeleteTeam, onManagePlayers, onExportTeam,
  onToggleTeamLock, onOpenChat, onRemovePlayerFromTeam
}) => (
  <Card>
    <CardHeader>
      <div className="flex justify-between items-center">
        <CardTitle>Teams</CardTitle>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Label className="text-sm">Age:</Label>
            <Select value={teamsAgeFilter} onValueChange={setTeamsAgeFilter}>
              <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {tournament.age_categories?.map(cat => (<SelectItem key={cat} value={cat}>{cat.toUpperCase()}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <Button size="sm" onClick={onCreateTeam} data-testid="create-team-btn"><Plus className="h-4 w-4 mr-1" />Create Team</Button>
        </div>
      </div>
    </CardHeader>
    <CardContent>
      {filteredTeams.length === 0 ? (
        <div className="text-center py-8 text-slate-500">
          <Shield className="h-12 w-12 mx-auto mb-3 text-slate-300" />
          <p>No teams created yet</p><p className="text-sm">Click "Create Team" to add a team</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredTeams.map(team => (
            <Card key={team.id} className={`hover:shadow-md transition-shadow ${team.is_locked ? 'border-amber-300 bg-amber-50/30' : ''}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <h4 className="font-semibold text-lg">{team.name}</h4>
                    <Badge variant="secondary">{team.age_category ? team.age_category.toUpperCase() : 'OPEN'}</Badge>
                    {team.division && <Badge variant="outline" className="text-xs">{team.division}</Badge>}
                    {team.is_locked && <Badge className="bg-amber-100 text-amber-700 text-xs"><Lock className="h-3 w-3 mr-1" />RSVPs Closed</Badge>}
                    {team.cricclubs_url && (
                      <a href={team.cricclubs_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium" data-testid={`cricclubs-link-${team.id}`}>
                        <ExternalLink className="h-3 w-3" />CricClubs
                      </a>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" onClick={() => onToggleTeamLock(team)} className={team.is_locked ? "text-green-600 hover:text-green-700" : "text-amber-600 hover:text-amber-700"} data-testid={`lock-team-${team.id}`}>
                      {team.is_locked ? (<><Unlock className="h-3 w-3 mr-1" />Open RSVPs</>) : (<><Lock className="h-3 w-3 mr-1" />Close RSVPs</>)}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => onExportTeam(team)} className="text-emerald-600 hover:text-emerald-700" data-testid={`export-team-${team.id}`}><Download className="h-3 w-3 mr-1" />Export</Button>
                    <Button size="sm" variant="outline" onClick={() => onManagePlayers(team)} className="text-blue-600 hover:text-blue-700" data-testid={`manage-players-${team.id}`}><Users className="h-3 w-3 mr-1" />Manage</Button>
                    <Button size="sm" variant="outline" onClick={() => onEditTeam(team)} data-testid={`edit-team-${team.id}`}><Edit className="h-3 w-3 mr-1" />Edit</Button>
                    <Button size="sm" variant="outline" onClick={() => onOpenChat(team.id)} className="text-blue-600 hover:text-blue-700" data-testid={`chat-team-${team.id}`}><MessageCircle className="h-3 w-3 mr-1" />Chat</Button>
                    <Button size="sm" variant="outline" onClick={(e) => onDeleteTeam(team, e)} className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`delete-team-${team.id}`}><Trash2 className="h-3 w-3" /></Button>
                  </div>
                </div>
                <p className="text-sm text-slate-600 mb-3">Coach: {team.coach_name || 'Not assigned'} - {team.player_count || 0} players</p>
                <div className="border-t pt-3">
                  <button onClick={() => setExpandedTeamId(expandedTeamId === team.id ? null : team.id)} className="flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-slate-800 w-full" data-testid={`toggle-roster-${team.id}`}>
                    {expandedTeamId === team.id ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    <span>ROSTER ({team.player_count || 0})</span>
                    {team.players?.length > 0 && <span className="text-xs text-slate-400 ml-2">Click to {expandedTeamId === team.id ? 'collapse' : 'expand'}</span>}
                  </button>
                  {expandedTeamId === team.id && team.players?.length > 0 && (
                    <div className="mt-3">
                      <div className="flex items-center gap-2 px-2 py-2 bg-slate-100 rounded-t text-xs font-medium text-slate-600">
                        <button onClick={() => toggleRosterSort('player_name')} className="flex items-center gap-1 hover:text-slate-900 flex-1" data-testid="sort-by-name">
                          Player Name<ArrowUpDown className={`h-3 w-3 ${teamRosterSort.field === 'player_name' ? 'text-blue-600' : ''}`} />
                          {teamRosterSort.field === 'player_name' && <span className="text-blue-600">({teamRosterSort.order})</span>}
                        </button>
                        <button onClick={() => toggleRosterSort('payment_status')} className="flex items-center gap-1 hover:text-slate-900 w-24 justify-end" data-testid="sort-by-payment">
                          Payment<ArrowUpDown className={`h-3 w-3 ${teamRosterSort.field === 'payment_status' ? 'text-blue-600' : ''}`} />
                        </button>
                      </div>
                      <div className="border border-t-0 rounded-b max-h-[300px] overflow-y-auto">
                        {sortTeamRoster(team.players).map((p, idx) => (
                          <div key={p.id || p.player_id} className={`flex items-center justify-between gap-2 py-2 px-3 text-sm ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}`}>
                            <Link to={`/admin/players/${p.player_id || p.id}`} className="font-medium text-blue-600 hover:text-blue-700 hover:underline flex-1 truncate" data-testid={`roster-player-link-${p.player_id || p.id}`}>{p.player_name || p.full_name}</Link>
                            <Badge className={`text-xs shrink-0 ${p.payment_status === 'paid' ? 'bg-green-100 text-green-700' : p.payment_status === 'pending' ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`}>
                              {p.payment_status === 'paid' ? 'Paid' : p.payment_status === 'pending' ? 'Pending' : 'Unpaid'}
                            </Badge>
                            {onRemovePlayerFromTeam && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 w-7 p-0 text-slate-400 hover:text-rose-600 hover:bg-rose-50 shrink-0"
                                disabled={team.is_locked}
                                onClick={() => onRemovePlayerFromTeam(team, p)}
                                title={team.is_locked ? 'Team is locked — unlock to edit roster' : `Remove ${p.player_name || p.full_name} from ${team.name} (keeps tournament registration)`}
                                data-testid={`remove-player-from-team-${team.id}-${p.player_id || p.id}`}
                              >
                                <UserMinus className="h-3.5 w-3.5" />
                              </Button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {expandedTeamId !== team.id && team.players?.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {team.players.slice(0, 3).map(p => (
                        <div key={p.id || p.player_id} className="flex items-center justify-between py-1 px-2 bg-slate-50 rounded text-sm">
                          <span className="truncate flex-1 text-slate-700">{p.player_name || p.full_name}</span>
                          <Badge className={`ml-2 text-xs ${p.payment_status === 'paid' ? 'bg-green-100 text-green-700' : p.payment_status === 'pending' ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`}>
                            {p.payment_status === 'paid' ? '\u2713' : p.payment_status === 'pending' ? '...' : '\u2717'}
                          </Badge>
                        </div>
                      ))}
                      {team.players.length > 3 && <p className="text-xs text-slate-400 pl-2">+{team.players.length - 3} more players...</p>}
                    </div>
                  )}
                  {(!team.players || team.players.length === 0) && <p className="text-xs text-slate-400 italic mt-2">No players assigned</p>}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </CardContent>
  </Card>
);
